'use client';

import { createContext, PropsWithChildren, useContext, useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import { LngLatBoundsLike } from 'react-map-gl/mapbox';

import { fetchJson } from 'lib/fetch-json';
import { citiesMapFromLocations, GroupedLocationsType, groupLocationsByCity } from '../app/locatii/utils';

export type ViewState = { latitude: number; longitude: number; zoom: number };

type MapStateType = {
    initialized: boolean;
    accessToken: string;
    bounds: LngLatBoundsLike | undefined;
    locations: any;
    allLocations: GroupedLocationsType | null;
    hoveredItem: any;
    renderedLocations: any[] | null;
    viewState?: ViewState;
    stateCitiesMap: any;
    setInitialized: (initialized: boolean) => void;
    setBounds: (bounds: LngLatBoundsLike) => void;
    setViewState: (viewState: ViewState) => void;
    setHoveredItem: (hoveredItem: any) => void;
    setRenderedLocations: (renderedLocations: any) => void;
};

const MapStateContext = createContext<MapStateType>({
    accessToken: '',
    allLocations: null,
    bounds: undefined,
    hoveredItem: null,
    initialized: false,
    locations: null,
    renderedLocations: null,
    stateCitiesMap: null,
    setBounds: () => {},
    setInitialized: () => {},
    setHoveredItem: () => {},
    setRenderedLocations: () => {},
    setViewState: () => {},
});

type Props = PropsWithChildren<{
    accessToken: string;
    defaultBounds?: LngLatBoundsLike;
}>;

export function MapStateProvider(props: Props) {
    const { data: locations } = useSWR<any>('/api/locations-geojson', fetchJson, {
        revalidateIfStale: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
    });
    const { defaultBounds = [20.60277870005361, 43.79419507544688, 28.81263, 47.80394203405562], ...rest } = props;
    const [bounds, _setBounds] = useState<LngLatBoundsLike>(defaultBounds);
    const [viewState, _setViewState] = useState<ViewState>();
    const [hoveredItem, _setHoveredItem] = useState(null);
    const [initialized, _setInitialized] = useState(false);
    const [allLocations, setAllLocations] = useState<GroupedLocationsType | null>(null);
    const [renderedLocations, _setRenderedLocations] = useState<any[] | null>(null);
    const [stateCitiesMap, setStateCitiesMap] = useState(null);

    useEffect(() => {
        if (locations) {
            const l = ((locations?.features || []) as any[])?.map((i) => i.properties);
            setAllLocations(groupLocationsByCity(l));
            setStateCitiesMap(citiesMapFromLocations(l));
        }
    }, [locations]);

    const value = useMemo<MapStateType>(
        () => ({
            accessToken: props.accessToken,
            allLocations,
            bounds,
            hoveredItem,
            initialized,
            locations,
            renderedLocations,
            viewState,
            stateCitiesMap,
            setBounds: (bounds: LngLatBoundsLike) => {
                _setBounds(bounds || defaultBounds);
            },
            setHoveredItem: (hoveredItem: any) => {
                _setHoveredItem(hoveredItem);
            },
            setInitialized: (initialized: boolean) => {
                _setInitialized(initialized);
            },
            setRenderedLocations: (locations: any[]) => {
                _setRenderedLocations(locations);
            },
            setViewState: (viewState: ViewState) => {
                _setViewState(viewState);
            },
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            allLocations,
            props.accessToken,
            locations,
            bounds,
            viewState,
            hoveredItem,
            renderedLocations,
            _setBounds,
            _setViewState,
            _setHoveredItem,
            _setRenderedLocations,
        ],
    );

    return <MapStateContext.Provider value={value} {...rest} />;
}

// a hook which we are going to use whenever we need data from `MapStateProvider`
export default function useMapState() {
    const context = useContext(MapStateContext);

    if (!context) {
        throw new Error('You need to wrap MapStateProvider.');
    }

    return context;
}
